// src/components/landing/HeroBlock.tsx
// Prefixo CSS: ldgpr- (hero)

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, BookOpen, HelpCircle, Calendar, BarChart2 } from 'lucide-react';
import './HeroBlock.css';

const FEATURE_LINKS = [
    { label: 'Flashcards Inteligentes', icon: Brain, blockId: 'bloco-flashcards' },
    { label: 'Apostilas Interativas', icon: BookOpen, blockId: 'bloco-apostilas' },
    { label: 'Banco de Questões', icon: HelpCircle, blockId: 'bloco-questoes' },
    { label: 'Assistente Graia', icon: Calendar, blockId: 'bloco-planner' },
    { label: 'Métricas e Grupos de Estudo', icon: BarChart2, blockId: 'bloco-desempenho' },
];

function scrollToBlock(id: string) {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

const HeroBlock: React.FC = () => {
    const navigate = useNavigate();
    const [activeFeature, setActiveFeature] = useState<string | null>(null);

    return (
        <section className="ldgpr-hero">
            <div className="ldgpr-hero-bg" aria-hidden="true">
                <div className="ldgpr-hero-glow ldgpr-hero-glow--1" />
                <div className="ldgpr-hero-glow ldgpr-hero-glow--2" />
                <div className="ldgpr-hero-grid" />
                <div className="ldgpr-hero-vline ldgpr-hero-vline--2" />
            </div>

            <div className="ldgpr-hero-split">

                {/* ── ESQUERDA ── */}
                <div className="ldgpr-hero-left">
                    <h1 className="ldgpr-hero-title">
                        Seu estudo <em className="ldgpr-hero-title-em">finalmente</em><br />
                        sob controle.
                    </h1>

                    <p className="ldgpr-hero-subtitle">
                        Uma plataforma de estudo construída para a graduação em medicina.
                    </p>

                    <ul className="ldgpr-hero-features">
                        {FEATURE_LINKS.map(f => {
                            const Icon = f.icon;
                            return (
                                <li
                                    key={f.blockId}
                                    className={`ldgpr-feat-item ${activeFeature === f.blockId ? 'ldgpr-feat-item--active' : ''}`}
                                    onClick={() => {
                                        setActiveFeature(f.blockId);
                                        scrollToBlock(f.blockId);
                                        setTimeout(() => setActiveFeature(null), 1200);
                                    }}
                                >
                                    <span className="ldgpr-feat-icon">
                                        <Icon size={13} strokeWidth={1.75} />
                                    </span>
                                    <span className="ldgpr-feat-label">{f.label}</span>
                                    <span className="ldgpr-feat-arrow">→</span>
                                </li>
                            );
                        })}
                    </ul>

                    <div className="ldgpr-hero-actions">
                        <button className="ldgpr-cta-primary" onClick={() => navigate('/cadastro')}>
                            Começar gratuitamente
                            <span className="ldgpr-cta-arrow">→</span>
                        </button>
                        <button className="ldgpr-cta-secondary" onClick={() => navigate('/marketplace')}>
                            Catálogo
                        </button>
                    </div>

                    <p className="ldgpr-hero-footnote">Sem cartão de crédito.</p>
                </div>

                {/* ── DIREITA: SMARTPHONE + CARD ── */}
                <div className="ldgpr-hero-right">
                    <div className="ldgpr-hero-stack">

                        {/* Smartphone decorativo — atrás, animação por último */}
                        <div className="ldgpr-phone ldgpr-phone--bg">
                            <div className="ldgpr-phone-notch" />
                            <div className="ldgpr-phone-screen">
                                <div className="ldgpr-app-header">
                                    <span className="ldgpr-app-logo">GRAIA</span>
                                    <span className="ldgpr-app-sep" />
                                    <span className="ldgpr-app-section">Banco de Questões</span>
                                </div>
                                <div className="ldgpr-app-body">
                                    <span className="ldgpr-app-tag">Geral · Difícil</span>
                                    <p className="ldgpr-app-question">
                                        Qual a melhor plataforma para estudantes de medicina?
                                    </p>
                                    <div className="ldgpr-app-options">
                                        {['Alternativa A', 'Alternativa B', 'GRAIA', 'Alternativa D'].map((opt, i) => (
                                            <div
                                                key={opt}
                                                className={`ldgpr-app-option ${i === 2 ? 'ldgpr-app-option--correct' : ''}`}
                                            >
                                                <span className="ldgpr-app-opt-key">{['A', 'B', 'C', 'D'][i]}</span>
                                                <span className="ldgpr-app-opt-text">{opt}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="ldgpr-phone-home" />
                        </div>

                        {/* Card principal */}
                        <div className="ldgpr-hv-main">

                            {/* Float topo — Próxima revisão: subject + when na mesma linha */}
                            <div className="ldgpr-hv-float ldgpr-hv-float--top">
                                <div className="ldgpr-hv-float-accent" />
                                <div className="ldgpr-hv-float-body">
                                    <span className="ldgpr-hv-float-label">Próxima revisão</span>
                                    <div className="ldgpr-hv-float-subject-row">
                                        <span className="ldgpr-hv-float-subject">ECG</span>
                                        <span className="ldgpr-hv-float-when">em 2 dias</span>
                                    </div>
                                </div>
                            </div>

                            <div className="ldgpr-hv-main-header">
                                <span className="ldgpr-hv-logo">GRAIA</span>
                                <span className="ldgpr-hv-header-sep" />
                                <span className="ldgpr-hv-mode">Revisão Adaptativa</span>
                                <span className="ldgpr-hv-counter">4 / 12</span>
                            </div>

                            <div className="ldgpr-hv-mastery">
                                <div className="ldgpr-hv-mastery-row">
                                    <span>Domínio</span>
                                    <strong>68%</strong>
                                </div>
                                <div className="ldgpr-hv-mastery-track">
                                    <div className="ldgpr-hv-mastery-fill" />
                                </div>
                            </div>

                            <div className="ldgpr-hv-card-content">
                                <span className="ldgpr-hv-tag">Neurologia · Doenças Neuromusculares</span>
                                <p className="ldgpr-hv-question">
                                    Paciente com ptose, diplopia e fraqueza muscular que piora ao longo do dia.
                                    Qual é o principal mecanismo fisiopatológico?
                                </p>
                                <div className="ldgpr-hv-answer">
                                    <span className="ldgpr-hv-answer-label">Resposta</span>
                                    <p className="ldgpr-hv-answer-text">
                                        Autoanticorpos contra receptores nicotínicos de acetilcolina
                                        na junção neuromuscular (Miastenia Gravis).
                                    </p>
                                </div>
                            </div>

                            <div className="ldgpr-hv-ratings">
                                {[
                                    { n: 1, l: 'Errei', c: '#dc2626' },
                                    { n: 2, l: 'Difícil', c: '#ea580c' },
                                    { n: 3, l: 'Ok', c: '#ca8a04' },
                                    { n: 4, l: 'Fácil', c: '#16a34a' },
                                    { n: 5, l: 'Ótimo', c: '#184C18' },
                                ].map(r => (
                                    <button
                                        key={r.n}
                                        className="ldgpr-hv-rating"
                                        style={{ '--rc': r.c } as React.CSSProperties}
                                    >
                                        <span>{r.n}</span>
                                        <span>{r.l}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Float base — Sessão ativa */}
                            <div className="ldgpr-hv-float ldgpr-hv-float--bottom">
                                <span className="ldgpr-hv-session-dot" />
                                <div className="ldgpr-hv-float-body">
                                    <span className="ldgpr-hv-float-label">Sessão ativa</span>
                                    <span className="ldgpr-hv-session-time">00:14:32</span>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default HeroBlock;